<script setup>
import ConfirmDialog from "primevue/confirmdialog";
import { useConfirm } from "primevue/useconfirm";
import BaseButton from "@/Components/UI/buttons/BaseButton.vue";
import BaseIcon from "@/Components/UI/BaseIcon.vue";
import BaseModalPanel from "@/Components/UI/modals/BaseModalPanel.vue";
import BaseConfirmBody from "@/Components/UI/modals/BaseConfirmBody.vue";

// Mounted once in the persistent layout. ConfirmAction routes every confirm here via
// useConfirm.require({ group: 'korgi-confirm', ... }); the #container slot reads the
// per-call fields off `message`.
//
// This dialog is service-owned on purpose — it must outlive the component that opened
// it (a kebab Popover unmounts its trigger on outside-click). That is why it can't
// simply render BaseModal. Instead it renders the SAME panel and body components as
// BaseModal-based modals, so chrome, paddings, × placement, artwork size and the
// button row match by construction.
const isDanger = (m) => m?.tone !== "neutral";
const neutralIcon = (m) => m?.icon ?? "circle-help";

// Click on the backdrop (not the panel) closes the dialog — ConfirmDialog has no
// dismissableMask prop, so wire it via the mask's onClick. currentTarget is the mask;
// a click that bubbled up from the panel has a different target and is ignored.
const confirm = useConfirm();
const onMaskClick = (e) => {
    if (e.target === e.currentTarget) {
        confirm.close();
    }
};
</script>

<template>
    <!--
      Top anchoring AND the gap both live here, on the mask — never on anything
      derived from `message`. Closing via the backdrop calls confirm.close(), which
      clears the message at the START of the leave transition, so a position-derived
      `p-dialog-top` took its align-items with it (panel slid to mid-screen) and then
      its top offset too (panel jumped up ~14px). Byte-for-byte the same mask classes
      BaseModal uses, so a confirm and a regular modal now sit on the same line.
      !items-start is !important because PrimeVue sets align-items inline.
    -->
    <ConfirmDialog
        group="korgi-confirm"
        :pt="{ mask: { class: '!items-start p-4 md:p-10 md:py-15 overflow-y-auto', onClick: onMaskClick } }">
        <template #container="{ message, acceptCallback, rejectCallback }">
            <BaseModalPanel
                title-in-body
                class="md:max-w-md"
                @close="rejectCallback">
                <BaseConfirmBody
                    :title="message.text"
                    :desc="message.desc">
                    <template #artwork>
                        <img
                            v-if="isDanger(message)"
                            src="/storage/images/confirm-delete.svg"
                            class="w-32 h-32"
                            alt="" />
                        <span
                            v-else
                            class="flex items-center justify-center w-32 h-32 rounded-full bg-blue-5 text-accent-blue">
                            <BaseIcon
                                :name="neutralIcon(message)"
                                class="w-15 h-15" />
                        </span>
                    </template>

                    <template #buttons>
                        <BaseButton
                            variant="secondary"
                            class="w-full"
                            @click="rejectCallback">
                            Отмена
                        </BaseButton>
                        <BaseButton
                            :variant="isDanger(message) ? 'danger' : 'primary'"
                            :disabled="message.disabled"
                            class="w-full"
                            @click="acceptCallback">
                            {{ message.btn }}
                        </BaseButton>
                    </template>
                </BaseConfirmBody>
            </BaseModalPanel>
        </template>
    </ConfirmDialog>
</template>
